/**
 * Campanhas — a listagem.
 *
 * Cards, não tabela: o que distingue uma campanha é a cadência da régua
 * (quantos toques, em que canal, com que espera), e isso é uma faixa visual,
 * não uma célula. Os números são reais e dizem a verdade — uma campanha que
 * nunca disparou mostra "nunca disparada", não um zero fingindo atividade.
 */

import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api, type Lista } from '../../api/cliente'
import { Botao, Cartao, Carregando, Etiqueta, Vazio, formatarData } from '../../componentes/ui'
import { FluxoRegua } from './FluxoRegua'
import { NovaCampanha } from './NovaCampanha'
import { TONS_STATUS, n, type Campanha } from './tipos'

export function ListagemCampanhas() {
  const [criando, setCriando] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['campanhas'],
    queryFn: () => api.get<Lista<Campanha>>('/api/v1/campanhas'),
  })

  if (isLoading) return <Carregando />

  return (
    <div className="space-y-6">
      <Cartao
        titulo="Campanhas"
        descricao="Cada campanha tem uma régua de automação. Nada sai sem passar pelo gate eleitoral, pelo opt-in e pela supressão — e disparar é sempre uma decisão manual."
        acoes={<Botao onClick={() => setCriando(true)}>Nova campanha</Botao>}
      >
        {data?.dados.length ? (
          <div className="grid gap-4 lg:grid-cols-2">
            {data.dados.map((c) => {
              const totalEnvios =
                c.metricas.enviados + c.metricas.bloqueados + c.metricas.falhas
              return (
                <Link
                  key={c.id}
                  to={`/campanhas/${c.id}`}
                  className="flex flex-col gap-3 rounded-xl border border-[color:var(--color-borda)] p-5 transition hover:border-marca-300 hover:shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900">{c.nome}</p>
                      {c.objetivo && (
                        <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-slate-500">
                          {c.objetivo}
                        </p>
                      )}
                    </div>
                    <Etiqueta tom={TONS_STATUS[c.status] ?? 'neutro'}>{c.status}</Etiqueta>
                  </div>

                  <FluxoRegua fluxo={c.fluxo} />

                  <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[color:var(--color-borda-suave)] pt-3 text-xs text-slate-500">
                    <span className="capitalize">{c.classeComunicacao}</span>
                    {c.execucoes.naRegua > 0 && (
                      <span>
                        <strong className="tabular">{n(c.execucoes.naRegua)}</strong> na régua
                        {c.execucoes.emAndamento > 0 &&
                          ` · ${n(c.execucoes.emAndamento)} em andamento`}
                      </span>
                    )}
                    {totalEnvios > 0 ? (
                      <span>
                        <strong className="tabular">{n(c.metricas.enviados)}</strong> enviados
                        {c.metricas.cliques > 0 && ` · ${n(c.metricas.cliques)} clicaram`}
                        {c.metricas.conversoes > 0 && ` · ${n(c.metricas.conversoes)} preencheram`}
                        {c.metricas.bloqueados + c.metricas.falhas > 0 &&
                          ` · ${n(c.metricas.bloqueados + c.metricas.falhas)} bloqueados`}
                      </span>
                    ) : (
                      <span className="text-slate-400">nunca disparada</span>
                    )}
                    <span className="ml-auto">{formatarData(c.atualizadoEm)}</span>
                  </div>
                </Link>
              )
            })}
          </div>
        ) : (
          <Vazio
            titulo="Nenhuma campanha ainda"
            descricao="Crie uma campanha, desenhe a régua e simule antes de disparar."
            acao={<Botao onClick={() => setCriando(true)}>Nova campanha</Botao>}
          />
        )}
      </Cartao>

      <NovaCampanha aberto={criando} aoFechar={() => setCriando(false)} />
    </div>
  )
}
